// Backend still uses /api/restaurant* URL paths (D-13 backend rename deferred).
// Method names here are renamed to client-*, but URL strings target the real backend path.
/**
 * API client for Dashboard
 */

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://dashboard.ava.swr.ai';

export type UserRole = 'admin' | 'business_owner' | 'business_staff' | 'agent' | 'referral';

export interface User {
  user_id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: UserRole;
  client_id: number | null;
  referral_id: number | null;
  /** Phase 4.9 — when set, dashboard calendar is scoped to this resource. */
  resource_id: number | null;
  restaurant_name: string | null;
  client_type: 'restaurant' | 'support_center' | 'municipal_portal' | 'service' | null;
}

export interface ReferralRow {
  referral_id: number;
  phone_number: string | null;
  commission_percent: number;
  user_id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  clients_count?: number;
}

export interface ReferralClient {
  client_id: number;
  business_name: string;
  city: string | null;
  status: 'active' | 'suspended' | 'disabled';
  client_type: string | null;
  subscription_price: number;
  subscription_name: string | null;
  created_at: string | null;
}

export interface ReferralStats {
  clients: ReferralClient[];
  commission_percent: number;
  kpis: {
    total_clients: number;
    active_clients: number;
    commission_this_month: number;
    commission_all_time: number;
  };
  monthly_commission: Array<{ month: string; amount: number }>;
  monthly_activity: Array<{ month: string; calls: number; orders: number; schedules: number }>;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export class ApiError extends Error {
  status: number;
  detail: string;
  /** Original detail payload from the server — may be a string OR a structured object
   * (e.g. 409 conflicts return { message, existing_job_id }). Use this to pull
   * structured fields when status is known to return one. */
  rawDetail: unknown;

  constructor(status: number, detail: unknown) {
    const detailStr = typeof detail === 'string'
      ? detail
      : (detail && typeof detail === 'object' && 'message' in detail && typeof (detail as {message: unknown}).message === 'string')
        ? (detail as {message: string}).message
        : JSON.stringify(detail);
    super(detailStr);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detailStr;
    this.rawDetail = detail;
  }
}

// Agent Portal Types
export interface GorgiasTicket {
  id: number;
  subject: string | null;
  status: string;
  channel: string;
  customer_id: number | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  created_datetime: string;
  updated_datetime: string;
  messages_count: number;
}

export interface GorgiasMessage {
  id: number;
  body_text: string | null;
  body_html: string | null;
  channel: string;
  via: string;
  sender_type: string;
  sender_name: string | null;
  created_datetime: string;
  attachments?: Array<{
    url?: string;
    content_type?: string;
    name?: string;
  }>;
}

export interface AgentCall {
  call_id: number;
  client_id: number;
  client_name: string | null;
  agent_user_id: number;
  agent_name: string | null;
  to_number: string;
  from_number: string | null;
  call_sid: string | null;
  status: string;
  direction: string;
  gorgias_ticket_id: number | null;
  gorgias_customer_id: number | null;
  customer_name: string | null;
  disposition: string | null;
  notes: AgentCallNote[] | null;
  duration_seconds: number | null;
  recording_url: string | null;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentCallNote {
  note_id: number;
  call_id: number;
  note_text: string;
  created_by_id: number | null;
  created_by_name: string | null;
  created_at: string;
}

// Phase 5.5
export interface ServiceStatsResponse {
  client_id: number;
  window_days: number;
  totals: {
    total: number;
    active: number;
    cancelled: number;
    no_show: number;
    late_cancel: number;
  };
  rates: {
    cancel_rate: number;
    no_show_rate: number;
    late_cancel_rate: number;
  };
  by_source: Record<string, number>;
  per_resource: Array<{
    resource_id: number;
    name: string;
    booked_minutes: number;
    booked_count: number;
  }>;
}

// Phase 6.5 — organization-type catalog entry as returned by
// GET /api/organization_types. `supported` is annotated per `?client_type=`
// (currently only `service` carves anything out, via the Decision 0.7
// blocklist); when omitted on the request all rows come back supported.
export interface OrganizationTypeOption {
  organization_type_id: number;
  name: string;
  description: string | null;
  supported: boolean;
  reason?: string;
}

export interface ScheduleItem {
  schedule_id: number;
  client_id: number;
  customer_id: number;
  customer_name: string | null;
  customer_phone: string | null;
  service_id: number | null;
  service_name: string | null;
  service_color?: string | null;
  resource_id: number | null;
  resource_name: string | null;
  location_id: number | null;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  notes: string | null;
  business_name?: string;
  client_type?: string;
}

export interface CustomerItem {
  customer_id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  full_address: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string;
  rating: number | null;
}

export interface ResourceItem {
  resource_id: number;
  client_id: number;
  name: string;
  resource_type: 'staff' | 'master' | 'table';
  location_id: number | null;
  capacity: number | null;
  status: 'active' | 'inactive';
}

export interface LocationItem {
  location_id: number;
  client_id: number;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  phone: string | null;
  is_default: boolean;
  operating_hours: Record<string, unknown> | null;
  timezone: string;
}

export interface ServiceItem {
  service_id: number;
  client_id: number;
  name: string;
  default_duration: number | null;
  is_active: boolean;
  // Phase 4.2 — extended service catalog fields.
  price_cents?: number | null;
  description?: string | null;
  color?: string | null;
  buffer_before_minutes?: number | null;
  buffer_after_minutes?: number | null;
  is_walk_in_eligible?: boolean | null;
}

export interface ScheduleException {
  exception_id: number;
  client_id: number;
  datetime_start: string;  // "YYYY-MM-DD HH:MM:SS"
  datetime_end: string;    // "YYYY-MM-DD HH:MM:SS"
  reason: string | null;
  location_id: number | null;
  type: string;
}

export interface ServiceResourcePair {
  service_resource_id: number;
  service_id: number;
  resource_id: number;
  price_override_cents: number | null;
  duration_override_minutes: number | null;
}

export interface ServicePolicy {
  cancellation_window_hours: number;
  no_show_fee_percent: number;
  late_threshold_minutes: number;
  allow_walk_ins: boolean | number;
  default_reminder_24h: boolean | number;
  default_reminder_2h: boolean | number;
}

// Phase 4.3 — resource scheduling.
export interface WorkingHoursEntry {
  working_hours_id?: number;
  day_of_week: number;   // 1=Mon … 7=Sun
  time_start: string;    // "HH:MM" or "HH:MM:SS"
  time_end: string;
}

export interface TimeOffEntry {
  id: number;
  resource_id: number;
  datetime_start: string;  // ISO
  datetime_end: string;
  reason: string | null;
}

// ── Twilio number provisioning ────────────────────────────────────
export interface TwilioAvailableNumber {
  phone_number_e164: string;
  friendly_name: string;
  locality: string | null;
  region: string | null;
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  };
  monthly_cost_usd: number;
}

export interface TwilioPurchaseResult {
  phone_sid: string;
  phone_number_e164: string;
  monthly_cost_usd: number;
  configured_voice_url: boolean;
  warnings: string[];
}

export interface ProvisionFilesResult {
  success: boolean;
  client_id: number;
  target_dir: string | null;
  files_created: string[];
  files_skipped: string[];
  warnings: string[];
  error: string | null;
}

// ── Provisioning job types ──────────────────────────────────────────
export type JobStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
export type StepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export interface JobStep {
  name: string;
  state: StepState;
  started_at: string | null;
  finished_at: string | null;
  message: string | null;
}

export interface JobSnapshot {
  job_id: number;
  client_id: number;
  requested_by: number | null;
  status: JobStatus;
  options: Record<string, unknown>;
  steps: JobStep[];
  error: string | null;
  previous_job_id: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface ProvisionRequest {
  install_files?:               boolean;
  install_systemd?:             boolean;
  install_nginx?:               boolean;
  verify_service?:              boolean;
  configure_twilio_voice_url?:  boolean;
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // Load tokens from localStorage on init (client-side only)
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('access_token');
      this.refreshToken = localStorage.getItem('refresh_token');
    }
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      // Also set cookie for middleware (expires in 30 days)
      document.cookie = `access_token=${accessToken}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Strict`;
    }
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      // Clear cookie
      document.cookie = 'access_token=; path=/; max-age=0';
    }
  }

  getAccessToken() {
    return this.accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401 && this.refreshToken) {
      // Try to refresh token
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        // Retry original request
        (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (!retryResponse.ok) {
          const error = await retryResponse.json().catch(() => ({}));
          throw new ApiError(retryResponse.status, error.detail ?? 'Request failed');
        }
        return retryResponse.json();
      } else {
        this.clearTokens();
        throw new ApiError(401, 'Session expired');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(response.status, error.detail ?? 'Request failed');
    }

    return response.json();
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false;

    try {
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });

      if (!response.ok) return false;

      const data: LoginResponse = await response.json();
      this.setTokens(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    }
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<LoginResponse> {
    const data = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setTokens(data.access_token, data.refresh_token);
    return data;
  }

  async logout(): Promise<void> {
    if (this.refreshToken) {
      try {
        await this.request('/api/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: this.refreshToken }),
        });
      } catch {
        // Ignore errors on logout
      }
    }
    this.clearTokens();
  }

  async getMe(): Promise<User> {
    return this.request<User>('/api/auth/me');
  }

  // Stats endpoints
  async getStats() {
    return this.request('/api/stats');
  }

  async getClients(view: 'active' | 'deactivated' = 'active') {
    return this.request(`/api/restaurants?view=${view}`);
  }

  async getClient(clientId: number) {
    return this.request(`/api/restaurants/${clientId}`);
  }

  async getSubscriptions(): Promise<{ subscription_id: number; subscription_name: string; subscription_price: number }[]> {
    return this.request('/api/subscriptions') as Promise<{ subscription_id: number; subscription_name: string; subscription_price: number }[]>;
  }

  async getOrders(params?: { client_id?: number; status?: string; location_id?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.client_id) searchParams.set('client_id', params.client_id.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.location_id) searchParams.set('location_id', params.location_id.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return this.request(`/api/orders${query ? `?${query}` : ''}`);
  }

  // Location endpoints
  async getLocations(clientId: number) {
    return this.request(`/api/clients/${clientId}/locations`);
  }

  async createLocation(clientId: number, data: { name: string; address?: string; city?: string; state?: string; zip_code?: string; phone?: string; is_default?: boolean }) {
    return this.request(`/api/clients/${clientId}/locations`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateLocation(locationId: number, data: Record<string, unknown>) {
    return this.request(`/api/locations/${locationId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deactivateLocation(locationId: number) {
    return this.request(`/api/locations/${locationId}`, { method: 'DELETE' });
  }

  async getMenuOverrides(locationId: number) {
    return this.request(`/api/locations/${locationId}/menu-overrides`);
  }

  async saveMenuOverride(locationId: number, data: { item_name: string; available: boolean; price_override?: number | null }) {
    return this.request(`/api/locations/${locationId}/menu-overrides`, { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteMenuOverride(locationId: number, overrideId: number) {
    return this.request(`/api/locations/${locationId}/menu-overrides/${overrideId}`, { method: 'DELETE' });
  }

  // Menu library endpoints
  async getMenus(clientId: number) {
    return this.request(`/api/clients/${clientId}/menus`);
  }

  async createMenu(clientId: number, data: { name: string; content?: string; is_default?: boolean }) {
    return this.request(`/api/clients/${clientId}/menus`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateMenu(menuId: number, data: { name?: string; content?: string; is_default?: boolean }) {
    return this.request(`/api/menus/${menuId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteMenu(menuId: number) {
    return this.request(`/api/menus/${menuId}`, { method: 'DELETE' });
  }

  // Restaurant policies library endpoints
  async getPolicies(clientId: number) {
    return this.request(`/api/clients/${clientId}/policies`);
  }

  async createPolicy(clientId: number, data: Record<string, unknown>) {
    return this.request(`/api/clients/${clientId}/policies`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePolicy(policyId: number, data: Record<string, unknown>) {
    return this.request(`/api/policies/${policyId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deletePolicy(policyId: number) {
    return this.request(`/api/policies/${policyId}`, { method: 'DELETE' });
  }

  async getCalls(params?: { client_id?: number; limit?: number; date_from?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.client_id) searchParams.set('client_id', params.client_id.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    const query = searchParams.toString();
    return this.request(`/api/calls${query ? `?${query}` : ''}`);
  }

  async retranscribeCall(callSid: string) {
    return this.request(`/api/calls/${callSid}/retranscribe`, { method: 'POST' });
  }

  async getCallTranscript(callSid: string) {
    return this.request(`/api/calls/${callSid}/transcript`);
  }

  // Client setup endpoints (backend path still uses /restaurant*)
  async getClientsStatus() {
    return this.request('/api/restaurant-status');
  }

  async getClientStatus(clientId: number) {
    return this.request(`/api/restaurant-status/${clientId}`);
  }

  async setupClient(data: {
    client_id?: number | null;
    business_name?: string;
    phone_number?: string;
    street?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    website?: string;
    twilio_phone?: string;
    twilio_account_sid?: string;
    twilio_auth_token?: string;
    menu?: string;
    ai_style?: string;
    voice_id?: number;
    available_pickup?: boolean;
    available_delivery?: boolean;
    available_schedule?: boolean;
  }) {
    return this.request('/api/restaurants/setup', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createClient(data: {
    business_name: string;
    first_name?:   string;
    last_name?:    string;
    phone_number?: string;
    email?:        string;
    website?:      string;
    street?:       string;
    city?:         string;
    state?:        string;
    zip_code?:     string;
    status?:       string;
    client_type?:  string;
    organization_type_id?: number | null;
    ava_settings?: Record<string, unknown>;
    owner: {
      email:       string;
      first_name?: string;
      last_name?:  string;
    };
    location: {
      name:                 string;
      street?:              string;
      city?:                string;
      state?:               string;
      zip_code?:            string;
      phone?:               string;
      timezone?:            string;
      operating_hours_json?: string;
      delivery?:            boolean;
      pickup?:              boolean;
      scheduling?:          boolean;
    };
  }): Promise<{
    success:         boolean;
    client_id:       number;
    business_name:   string;
    owner_user_id:   number;
    owner_email:     string;
    onboarding_link: string;
    email_sent:      boolean;
  }> {
    return this.request('/api/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async startClientService(clientId: number) {
    return this.request(`/api/restaurants/${clientId}/start`, { method: 'POST' });
  }

  async stopClientService(clientId: number) {
    return this.request(`/api/restaurants/${clientId}/stop`, { method: 'POST' });
  }

  async deactivateClient(clientId: number) {
    return this.request<{ success: boolean; message: string; already_deleted?: boolean }>(
      `/api/restaurant/${clientId}`,
      { method: 'DELETE' },
    );
  }

  async restoreClient(clientId: number) {
    return this.request<{ success: boolean; message: string; already_active?: boolean; service_started?: boolean }>(
      `/api/restaurant/${clientId}/restore`,
      { method: 'POST' },
    );
  }

  async purgeClient(clientId: number) {
    return this.request<{
      success: boolean;
      message: string;
      purged?: boolean;
      warnings?: Array<{ step: string; message: string }>;
      users_deleted?: number;
    }>(
      `/api/admin/clients/${clientId}/purge`,
      { method: 'DELETE' },
    );
  }

  async getVoices() {
    return this.request('/api/voices');
  }

  async getAvailablePort() {
    return this.request('/api/ports/available');
  }

  // Support center endpoints
  async getSupportSettings(clientId: number) {
    return this.request(`/api/support/${clientId}/settings`);
  }

  async updateSupportSettings(clientId: number, data: Record<string, unknown>) {
    return this.request(`/api/support/${clientId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async testShopifyConnection(clientId: number) {
    return this.request(`/api/support/${clientId}/test-shopify`, { method: 'POST' });
  }

  // Agent Portal - Gorgias endpoints
  async getGorgiasTickets(clientId: number, limit: number = 50): Promise<{ tickets: GorgiasTicket[] }> {
    return this.request(`/api/gorgias/tickets?client_id=${clientId}&limit=${limit}`);
  }

  async getGorgiasTicket(ticketId: number, clientId: number): Promise<GorgiasTicket> {
    return this.request(`/api/gorgias/tickets/${ticketId}?client_id=${clientId}`);
  }

  async getGorgiasTicketMessages(ticketId: number, clientId: number): Promise<{ messages: GorgiasMessage[] }> {
    return this.request(`/api/gorgias/tickets/${ticketId}/messages?client_id=${clientId}`);
  }

  async createGorgiasNote(ticketId: number, clientId: number, note: string): Promise<{ success: boolean }> {
    return this.request(`/api/gorgias/tickets/${ticketId}/note`, {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, note })
    });
  }

  async replyToGorgiasTicket(ticketId: number, clientId: number, message: string): Promise<{ success: boolean; message_id?: number }> {
    return this.request(`/api/gorgias/tickets/${ticketId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, message })
    });
  }

  async updateGorgiasTicketStatus(ticketId: number, status: 'open' | 'closed' | 'spam' | 'deleted'): Promise<{ success: boolean; ticket_id: number; status: string }> {
    return this.request(`/api/gorgias/tickets/${ticketId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  async searchGorgias(clientId: number, query: string): Promise<{ tickets: GorgiasTicket[] }> {
    return this.request(`/api/gorgias/search?client_id=${clientId}&query=${encodeURIComponent(query)}`);
  }

  // Agent Portal - Agent Calls endpoints
  async createOutboundCall(data: {
    to_number: string;
    gorgias_ticket_id?: number;
    gorgias_customer_id?: number;
  }): Promise<{ success: boolean; call_id: number; call_sid: string }> {
    return this.request('/api/agent-calls/outbound', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getAgentCalls(params?: { client_id?: number; agent_user_id?: number; status?: string; limit?: number }): Promise<{ calls: AgentCall[] }> {
    const searchParams = new URLSearchParams();
    if (params?.client_id) searchParams.set('client_id', params.client_id.toString());
    if (params?.agent_user_id) searchParams.set('agent_user_id', params.agent_user_id.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return this.request(`/api/agent-calls${query ? `?${query}` : ''}`);
  }

  async getAgentCall(callId: number): Promise<AgentCall> {
    return this.request(`/api/agent-calls/${callId}`);
  }

  async endAgentCall(callId: number): Promise<{ success: boolean }> {
    return this.request(`/api/agent-calls/${callId}/end`, { method: 'POST' });
  }

  async addAgentCallNote(callId: number, note: string): Promise<{ success: boolean; note_id: number }> {
    return this.request(`/api/agent-calls/${callId}/note`, {
      method: 'POST',
      body: JSON.stringify({ note_text: note })
    });
  }

  async setAgentCallDisposition(callId: number, disposition: string, notes?: string): Promise<{ success: boolean }> {
    return this.request(`/api/agent-calls/${callId}/disposition`, {
      method: 'PUT',
      body: JSON.stringify({ disposition, notes })
    });
  }

  // Twilio Voice SDK endpoints
  async getTwilioToken(): Promise<{ token: string; identity: string }> {
    return this.request('/api/twilio/token');
  }

  // ── Schedules ──────────────────────────────────────────────────
  async getSchedules(params?: { location_id?: number; date_from?: string; date_to?: string }) {
    const q = new URLSearchParams();
    if (params?.location_id) q.set('location_id', params.location_id.toString());
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    return this.request<{ schedules: ScheduleItem[] }>(`/api/schedules${q.toString() ? `?${q}` : ''}`);
  }

  async getAdminSchedules(params?: { client_id?: number; location_id?: number; status?: string; date_from?: string; date_to?: string }) {
    const q = new URLSearchParams();
    if (params?.client_id) q.set('client_id', params.client_id.toString());
    if (params?.location_id) q.set('location_id', params.location_id.toString());
    if (params?.status) q.set('status', params.status);
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    return this.request<{ schedules: ScheduleItem[] }>(`/api/admin/schedules${q.toString() ? `?${q}` : ''}`);
  }

  // Phase 5.5 — service-business operational metrics.
  async getServiceStats(clientId: number, days: number = 30) {
    const q = new URLSearchParams({ client_id: String(clientId), days: String(days) });
    return this.request<ServiceStatsResponse>(`/api/admin/service_stats?${q.toString()}`);
  }

  // Phase 6.5 — organization-type catalog (optionally annotated per client_type).
  async getOrganizationTypes(clientType?: string) {
    const q = new URLSearchParams();
    if (clientType) q.set('client_type', clientType);
    const qs = q.toString();
    return this.request<OrganizationTypeOption[]>(`/api/organization_types${qs ? `?${qs}` : ''}`);
  }

  async createSchedule(data: {
    customer_id?: number | null; customer_name: string; customer_phone: string;
    service_id?: number | null; resource_id?: number | null; location_id?: number | null;
    start_time: string; end_time: string; status?: string; notes?: string;
  }) {
    return this.request<{ schedule_id: number; success: boolean }>('/api/schedules', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateSchedule(scheduleId: number, data: Partial<{
    customer_id: number | null; customer_name: string; customer_phone: string;
    service_id: number | null; resource_id: number | null; location_id: number;
    start_time: string; end_time: string; status: string; notes: string;
  }>) {
    return this.request<{ success: boolean }>(`/api/schedules/${scheduleId}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async cancelSchedule(scheduleId: number) {
    return this.request<{ success: boolean }>(`/api/schedules/${scheduleId}`, { method: 'DELETE' });
  }

  // ── Customers ──────────────────────────────────────────────────
  async getCustomers(params?: { client_id?: number; search?: string }) {
    const q = new URLSearchParams();
    if (params?.client_id) q.set('client_id', params.client_id.toString());
    if (params?.search) q.set('search', params.search);
    return this.request<{ customers: CustomerItem[] }>(`/api/customers${q.toString() ? `?${q}` : ''}`);
  }

  async createCustomer(data: {
    client_id?: number; first_name: string; last_name: string;
    email?: string; phone?: string; full_address?: string; street?: string;
    city?: string; state?: string; zip_code?: string; country?: string; rating?: number;
  }) {
    return this.request<{ customer_id: number; success: boolean }>('/api/customers', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  // ── Resources ──────────────────────────────────────────────────
  async getResources(params?: { client_id?: number; location_id?: number }) {
    const q = new URLSearchParams();
    if (params?.client_id) q.set('client_id', params.client_id.toString());
    if (params?.location_id) q.set('location_id', params.location_id.toString());
    return this.request<{ resources: ResourceItem[] }>(`/api/resources${q.toString() ? `?${q}` : ''}`);
  }

  async createResource(data: { name: string; resource_type: string; location_id?: number | null; capacity?: number | null; client_id?: number }) {
    return this.request<{ resource_id: number; success: boolean }>('/api/resources', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateResource(resourceId: number, data: { name?: string; resource_type?: string; location_id?: number | null; capacity?: number | null; is_active?: boolean }) {
    return this.request<{ success: boolean }>(`/api/resources/${resourceId}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteResource(resourceId: number) {
    return this.request<{ success: boolean }>(`/api/resources/${resourceId}`, { method: 'DELETE' });
  }

  // ── Services ───────────────────────────────────────────────────
  async getServices(clientId?: number) {
    const q = clientId ? `?client_id=${clientId}` : '';
    return this.request<{ services: ServiceItem[] }>(`/api/services${q}`);
  }

  async createService(data: {
    name: string;
    default_duration?: number | null;
    client_id?: number;
    price_cents?: number | null;
    description?: string | null;
    color?: string | null;
    buffer_before_minutes?: number | null;
    buffer_after_minutes?: number | null;
    is_walk_in_eligible?: boolean | null;
  }) {
    return this.request<{ service_id: number; success: boolean }>('/api/services', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateService(serviceId: number, data: {
    name?: string;
    default_duration?: number | null;
    is_active?: boolean;
    price_cents?: number | null;
    description?: string | null;
    color?: string | null;
    buffer_before_minutes?: number | null;
    buffer_after_minutes?: number | null;
    is_walk_in_eligible?: boolean | null;
  }) {
    return this.request<{ success: boolean }>(`/api/services/${serviceId}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteService(serviceId: number) {
    return this.request<{ success: boolean }>(`/api/services/${serviceId}`, { method: 'DELETE' });
  }

  // ── Service ↔ Resource matrix (Phase 4.4) ────────────────────
  async getServiceResources(clientId?: number) {
    const q = clientId ? `?client_id=${clientId}` : '';
    return this.request<{ pairs: ServiceResourcePair[] }>(`/api/service_resources${q}`);
  }

  async setServiceResources(serviceId: number, resourceIds: number[]) {
    return this.request<{ success: boolean; count: number }>(
      `/api/services/${serviceId}/resources`,
      { method: 'PUT', body: JSON.stringify({ resource_ids: resourceIds }) },
    );
  }

  // ── Service policies (Phase 4.7) — schedule_v2_router 2.8 ────
  async getServicePolicies(clientId?: number) {
    const q = clientId ? `?client_id=${clientId}` : '';
    return this.request<{ policy: ServicePolicy | null }>(`/api/service_policies${q}`);
  }

  async updateServicePolicies(data: Partial<ServicePolicy>, clientId?: number) {
    // The router's PUT carries `client_id` in the body for admin override
    // and requires at least one field (empty body → 400).
    const body = clientId ? { ...data, client_id: clientId } : data;
    return this.request<{ success: boolean }>(`/api/service_policies`, {
      method: 'PUT', body: JSON.stringify(body),
    });
  }

  // ── Schedule mutations: reschedule + no-show (Phase 4.6) ──────
  async rescheduleSchedule(scheduleId: number, data: {
    new_date: string;            // YYYY-MM-DD
    new_start_time: string;      // HH:MM:SS
    new_end_time: string;        // HH:MM:SS
    new_resource_id?: number | null;
    reason?: string | null;
  }) {
    return this.request<{ success: boolean; schedule_id: number }>(
      `/api/schedules/${scheduleId}/reschedule`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  async markScheduleNoShow(scheduleId: number) {
    return this.request<{ success: boolean }>(
      `/api/schedules/${scheduleId}/no_show`,
      { method: 'POST' },
    );
  }

  // ── Resource schedule + ICS (Phase 4.3 / 4.10) ────────────────
  async getResourceWorkingHours(resourceId: number) {
    return this.request<{ working_hours: WorkingHoursEntry[] }>(
      `/api/resources/${resourceId}/working_hours`,
    );
  }

  async replaceResourceWorkingHours(resourceId: number, entries: WorkingHoursEntry[]) {
    return this.request<{ success: boolean; count: number }>(
      `/api/resources/${resourceId}/working_hours`,
      { method: 'PUT', body: JSON.stringify({ entries }) },
    );
  }

  async listResourceTimeOff(resourceId: number, params?: { date_from?: string; date_to?: string }) {
    const q = new URLSearchParams();
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    return this.request<{ time_off: TimeOffEntry[] }>(
      `/api/resources/${resourceId}/time_off${q.toString() ? `?${q}` : ''}`,
    );
  }

  async createResourceTimeOff(resourceId: number, data: {
    datetime_start: string; datetime_end: string; reason?: string | null;
  }) {
    return this.request<{ success: boolean; time_off_id: number }>(
      `/api/resources/${resourceId}/time_off`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  async deleteResourceTimeOff(resourceId: number, timeOffId: number) {
    return this.request<{ success: boolean; deleted: number }>(
      `/api/resources/${resourceId}/time_off/${timeOffId}`,
      { method: 'DELETE' },
    );
  }

  async getResourceCalendarToken(resourceId: number) {
    return this.request<{ token: string; url: string }>(
      `/api/resources/${resourceId}/calendar_token`,
    );
  }

  // ── Schedule Exceptions ────────────────────────────────────────
  async getScheduleExceptions(params?: { client_id?: number; location_id?: number }) {
    const q = new URLSearchParams();
    if (params?.client_id) q.set('client_id', params.client_id.toString());
    if (params?.location_id) q.set('location_id', params.location_id.toString());
    return this.request<{ exceptions: ScheduleException[] }>(`/api/schedule_exceptions${q.toString() ? `?${q}` : ''}`);
  }

  async createScheduleException(data: {
    datetime_start: string; datetime_end: string;
    reason?: string; location_id?: number | null; client_id?: number;
  }) {
    return this.request<{ exception_id: number; success: boolean }>('/api/schedule_exceptions', {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateScheduleException(exceptionId: number, data: { datetime_start: string; datetime_end: string; reason?: string }) {
    return this.request<{ success: boolean }>(`/api/schedule_exceptions/${exceptionId}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async deleteScheduleException(exceptionId: number) {
    return this.request<{ success: boolean }>(`/api/schedule_exceptions/${exceptionId}`, { method: 'DELETE' });
  }

  async updateProfile(data: { first_name?: string; last_name?: string; email?: string }) {
    return this.request<{ message: string }>('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: { current_password: string; new_password: string }) {
    return this.request<{ message: string }>('/api/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ── Referrals (admin) ──────────────────────────────────────────
  async listReferrals() {
    return this.request<ReferralRow[]>('/api/referrals');
  }

  async createReferral(data: {
    email: string;
    password: string;
    first_name?: string | null;
    last_name?: string | null;
    phone_number: string;
    commission_percent: number;
  }) {
    return this.request<{ referral_id: number; user_id: number; message: string }>('/api/referrals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReferral(referralId: number, data: {
    email?: string;
    first_name?: string | null;
    last_name?: string | null;
    password?: string;
    phone_number?: string;
    commission_percent?: number;
    is_active?: boolean;
  }) {
    return this.request<{ message: string }>(`/api/referrals/${referralId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteReferral(referralId: number) {
    return this.request<{ message: string }>(`/api/referrals/${referralId}`, { method: 'DELETE' });
  }

  // ── Referral portal ────────────────────────────────────────────
  async getReferralProfile() {
    return this.request<ReferralRow>('/api/referral/me');
  }

  async getReferralStats(clientId?: number | null) {
    const q = clientId != null ? `?client_id=${clientId}` : '';
    return this.request<ReferralStats>(`/api/referral/stats${q}`);
  }

  async createReferralClient(data: {
    business_name: string;
    first_name?:   string;
    last_name?:    string;
    phone_number?: string;
    email?:        string;
    website?:      string;
    street?:       string;
    city?:         string;
    state?:        string;
    zip_code?:     string;
    status?:       string;
    client_type?:  string;
    organization_type_id?: number | null;
    ava_settings?: Record<string, unknown>;
    owner: {
      email:       string;
      first_name?: string;
      last_name?:  string;
    };
    location: {
      name:                 string;
      street?:              string;
      city?:                string;
      state?:               string;
      zip_code?:            string;
      phone?:               string;
      timezone?:            string;
      operating_hours_json?: string;
      delivery?:            boolean;
      pickup?:              boolean;
      scheduling?:          boolean;
    };
  }): Promise<{
    success:         boolean;
    client_id:       number;
    business_name:   string;
    owner_user_id:   number;
    owner_email:     string;
    onboarding_link: string;
    email_sent:      boolean;
  }> {
    return this.request<{
      success:         boolean;
      client_id:       number;
      business_name:   string;
      owner_user_id:   number;
      owner_email:     string;
      onboarding_link: string;
      email_sent:      boolean;
    }>(
      '/api/referral/clients',
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  /** Wizard pre-flight: is this email available for a new business_owner? */
  async checkOwnerEmailAvailability(email: string): Promise<{
    available:     boolean;
    business_name: string | null;
  }> {
    const q = new URLSearchParams({ email });
    return this.request(`/api/users/email-availability?${q.toString()}`, { method: 'GET' });
  }

  // ── Owner onboarding (magic-link) ────────────────────────────────
  async inspectOnboardingToken(token: string): Promise<{
    email:      string;
    first_name: string | null;
    last_name:  string | null;
    expires_at: string;
  }> {
    return this.request(`/api/onboarding/${encodeURIComponent(token)}`, { method: 'GET' });
  }

  async consumeOnboardingToken(token: string, password: string): Promise<{
    access_token:  string;
    refresh_token: string;
    token_type:    string;
    expires_in:    number;
    user: {
      user_id:         number;
      email:           string;
      first_name:      string | null;
      last_name:       string | null;
      role:            string;
      client_id:       number | null;
      referral_id:     number | null;
      restaurant_name: string | null;
      client_type:     string | null;
    };
  }> {
    return this.request(`/api/onboarding/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async saveAvaSettings(clientId: number, data: Record<string, unknown>) {
    return this.request(`/api/restaurant/${clientId}/ava-settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ── Twilio number provisioning ──────────────────────────────────
  async searchTwilioNumbers(filters: {
    area_code?: string;
    contains?: string;
    in_region?: string;
    sms_enabled?: boolean;
    limit?: number;
  }): Promise<TwilioAvailableNumber[]> {
    const q = new URLSearchParams();
    if (filters.area_code) q.set('area_code', filters.area_code);
    if (filters.contains) q.set('contains', filters.contains);
    if (filters.in_region) q.set('in_region', filters.in_region);
    if (filters.sms_enabled !== undefined) q.set('sms_enabled', String(filters.sms_enabled));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    const query = q.toString();
    return this.request<TwilioAvailableNumber[]>(
      `/api/twilio/available-numbers${query ? `?${query}` : ''}`,
    );
  }

  async purchaseTwilioNumber(payload: {
    phone_number_e164: string;
    client_id: number;
    configure_voice_url: boolean;
  }): Promise<TwilioPurchaseResult> {
    return this.request<TwilioPurchaseResult>('/api/twilio/purchase-number', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ── Provisioning jobs ───────────────────────────────────────────
  async provisionClient(clientId: number, body: ProvisionRequest = {}): Promise<JobSnapshot> {
    return this.request<JobSnapshot>(`/api/clients/${clientId}/provision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async getProvisioningJob(jobId: number): Promise<JobSnapshot> {
    return this.request<JobSnapshot>(`/api/provisioning-jobs/${jobId}`);
  }

  async retryProvisioningJob(jobId: number): Promise<JobSnapshot> {
    return this.request<JobSnapshot>(`/api/provisioning-jobs/${jobId}/retry`, {
      method: 'POST',
    });
  }

  // ── Files-only provisioning ─────────────────────────────────────
  // Writes on-disk artifacts (target dir, app.py, settings.json, systemd
  // unit) for a freshly-created client. Idempotent — re-running on an
  // already-provisioned client returns success with files_skipped populated.
  async provisionClientFiles(clientId: number): Promise<ProvisionFilesResult> {
    return this.request<ProvisionFilesResult>(
      `/api/clients/${clientId}/provision-files`,
      { method: 'POST' },
    );
  }

  // Referral-scoped variants — same shape, but the backend uses platform
  // Twilio creds and gates purchase on clients.referral_id ownership.
  async searchReferralTwilioNumbers(filters: {
    area_code?: string;
    contains?: string;
    in_region?: string;
    sms_enabled?: boolean;
    limit?: number;
  }): Promise<TwilioAvailableNumber[]> {
    const q = new URLSearchParams();
    if (filters.area_code) q.set('area_code', filters.area_code);
    if (filters.contains) q.set('contains', filters.contains);
    if (filters.in_region) q.set('in_region', filters.in_region);
    if (filters.sms_enabled !== undefined) q.set('sms_enabled', String(filters.sms_enabled));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    const query = q.toString();
    return this.request<TwilioAvailableNumber[]>(
      `/api/referral/twilio/available-numbers${query ? `?${query}` : ''}`,
    );
  }

  async purchaseReferralTwilioNumber(payload: {
    phone_number_e164: string;
    client_id: number;
    configure_voice_url: boolean;
  }): Promise<TwilioPurchaseResult> {
    return this.request<TwilioPurchaseResult>('/api/referral/twilio/purchase-number', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export const api = new ApiClient();
